import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import translate from '../../../commonComponents/translate';
import ProductProp from '../propTypes/productVariationsProp';
import ShopProductImage from './ShopProductImage';
import {
  calculatePromoPercentage,
  newFormatPrice,
  promotionEndingMessage,
} from '../../../services/utilityFunctions';
import i18n from '../../../services/i18n';
import PriceDisplay from '../../../commonComponents/PriceDisplay';
import AddEditSection from './pack/advancedPackComponents/AddEditSection';
import UserProp from '../../../commonComponents/propTypes/userProp';
import { PACK_TYPE } from '../propTypes/productProp';

function ShopProductItem({
  t,
  product,
  isLandingPagePromotion,
  onBuyNow,
  onOpenAdvancedPackModal,
  loggedUser,
  journeySlug,
}) {
  const isAdvancedPack = product?.packType === PACK_TYPE.ADVANCED_PACK;
  const isHybridPack = product?.packType === PACK_TYPE.HYBRID_PACK;
  const productVariations = useMemo(() => {
    let variations = product.variations.filter(variation => variation.isPublic);
    // In case of a pack product
    if (product?.isPack) {
      // we can use the userPrice, userPromotionPrice, etc.
      // inside the product.pack, and override the normal variations, and code should handle every case
      variations = [product?.pack];
    }
    return variations;
  }, [product]);

  const getVariationPrice = useCallback(
    variation => {
      const {
        originalPriceWithVAT: priceTotalVAT,
        originalPriceWithoutVAT: priceTotalNoVAT,
        highlightedPrice,
      } = variation?.userPrice || {};
      const {
        originalPriceWithVAT: promotionTotalVAT,
        originalPriceWithoutVAT: promotionTotalNoVAT,
      } = variation?.userPromotionPrice || {};

      const showPricesWithoutVAT =
        highlightedPrice === 'originalPriceWithoutVAT';
      const mainPrice = showPricesWithoutVAT ? priceTotalNoVAT : priceTotalVAT;
      const minorPrice = showPricesWithoutVAT ? priceTotalVAT : priceTotalNoVAT;
      const mainPromoPrice = showPricesWithoutVAT
        ? promotionTotalNoVAT
        : promotionTotalVAT;
      const minorPromoPrice = showPricesWithoutVAT
        ? promotionTotalVAT
        : promotionTotalNoVAT;

      const hasValidPromoPrice =
        mainPromoPrice !== null &&
        mainPromoPrice !== undefined &&
        mainPromoPrice >= 0;
      const finalMainPrice = hasValidPromoPrice ? mainPromoPrice : mainPrice;
      const finalMinorPrice = hasValidPromoPrice ? minorPromoPrice : minorPrice;

      const vatInfo = !showPricesWithoutVAT
        ? t('vat.excluded.abbreviation')
        : t('vat.included.abbreviation');

      return {
        mainPrice: finalMainPrice >= 0 ? finalMainPrice : null,
        minorPrice: finalMinorPrice >= 0 ? finalMinorPrice : null,
        vatInfo,
      };
    },
    [t],
  );
  const returnPricesRange = useCallback(
    variations => {
      // No variations
      if (variations.length === 0) {
        return 'N/A';
      }

      const variationPrices = variations.map(v => getVariationPrice(v));
      const validPrices = variationPrices.filter(
        p => p.mainPrice !== null && p.mainPrice >= 0,
      );

      if (validPrices.length === 0) {
        return {
          mainPriceRange: 'N/A',
          minorPriceRange: '',
          vatInfo: '',
        };
      }

      const minMainPrice = Math.min(...validPrices.map(p => p.mainPrice));
      const minMinorPrice = Math.min(...validPrices.map(p => p.minorPrice));
      const vatInfo = validPrices[0]?.vatInfo || '';

      const showMinFree = minMainPrice === 0;

      return {
        mainPriceRange: showMinFree
          ? t('free')
          : newFormatPrice(
              minMainPrice,
              true,
              variations[0]?.userPrice?.code || '€',
            ),
        minorPriceRange: showMinFree
          ? t('free')
          : newFormatPrice(
              minMinorPrice,
              true,
              variations[0]?.userPrice?.code || '€',
            ),
        vatInfo: showMinFree ? '' : vatInfo,
      };
    },
    [getVariationPrice, t],
  );

  const maxDiscount = Math.floor(
    Math.max(
      ...productVariations
        .filter(v => v?.userPromotionPrice?.total)
        .map(v => {
          let discount = '';
          if (v?.userPromotionPrice?.total) {
            discount =
              ((v.userPrice.total - v.userPromotionPrice.total) /
                v.userPrice.total) *
              100;
          }
          return discount;
        }),
      0,
    ),
  );

  const renderDiscount = useCallback(() => {
    const isOnlyOneVariation = productVariations?.length === 1;
    if (isOnlyOneVariation) {
      const {
        promotionPriceDateEnd,
        userPrice,
        userPromotionPrice,
        promotionPriceDateStart,
        resellerPrice,
      } = productVariations[0];
      if (resellerPrice && loggedUser?.type === 'collaborator') {
        return null;
      }

      if (
        promotionPriceDateStart &&
        promotionPriceDateStart > moment().utc().valueOf()
      ) {
        return null;
      }
      const promotionMessage = promotionEndingMessage(promotionPriceDateEnd);
      const promoPercentage = calculatePromoPercentage(
        userPrice?.total,
        userPromotionPrice?.total,
      );
      const promoPercentageMessage = promoPercentage
        ? i18n.__('product.promotion.percentageOff', 100 - promoPercentage)
        : '';
      if (promotionPriceDateEnd && promotionMessage?.length > 0) {
        return (
          <div className="ribbon-target promotionEnding">
            {`${promoPercentageMessage} ${promotionMessage}`}
          </div>
        );
      }
    }

    if (!maxDiscount) {
      return null;
    }
    return (
      <div className="ribbon-target">
        {`${t('shop.product.save')} ${maxDiscount}%`}
      </div>
    );
  }, [t, productVariations]);

  const renderPromotionItem = () => (
    <div className="shop-overall-carousel">
      <div className="shop-overall-carousel-shop-item text-left">
        <div className="img-new-products">
          <ShopProductImage product={product} />
        </div>
        <div className="product-text px-4">
          {maxDiscount !== 0 && (
            <div className="shop-product-discount">
              <span>{i18n.__('shop.sale_up_to', maxDiscount)}</span>
            </div>
          )}
          <div className="shop-product-wrapper">
            <span className="shop-product-name">{product.name}</span>
          </div>
          <div className="shop-product-money">
            <div className="shop-price-list">
              <PriceDisplay
                t={t}
                userPrice={productVariations[0]?.userPrice}
                userPromotionPrice={productVariations[0]?.userPromotionPrice}
              />
            </div>
          </div>
          <div className="shop-buy-now">
            <div className="shop-buy-now-container">
              <a
                href={`/shop/product/${product.slug}`}
                className="shop-buy-now-button"
              >
                {t('shop.buyNow')}
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const minorPrice = returnPricesRange(productVariations).minorPriceRange
    ? `${returnPricesRange(productVariations).minorPriceRange} ${
        returnPricesRange(productVariations).vatInfo
      }`
    : 'N/A';

  const isEditable = useMemo(() => {
    if (journeySlug) {
      if (product?.packType === PACK_TYPE.HYBRID_PACK) {
        const availableSections = {};
        product?.multiPackVariationsAvailable?.forEach(variationSection => {
          const { _id: sectionId, amountVariationsToChoose } = variationSection;

          const quantitiesSelected =
            product?.packVariations
              ?.filter(variation => variation.sectionId === sectionId)
              ?.reduce(
                (acc, variation) => acc + (variation.quantity ?? 1),
                0,
              ) ?? 0;

          availableSections[sectionId] =
            quantitiesSelected === amountVariationsToChoose;
        });

        return Object.values(availableSections).every(v => v);
      }
      return product?.packVariations?.length > 0;
    }
    return product?.packVariations?.length > 0;
  }, [product, journeySlug]);

  const modPack = useMemo(() => {
    if (journeySlug) {
      return {
        ...product?.pack,
        amountVariationsPerAdvancedPack:
          product?.packType === PACK_TYPE.HYBRID_PACK
            ? product?.multiPackVariationsAvailable?.reduce(
                (acc, variation) => acc + variation.amountVariationsToChoose,
                0,
              )
            : product?.amountVariationsPerAdvancedPack,
      };
    }
    return {
      ...product?.pack,
      amountVariationsPerAdvancedPack:
        product?.pack?.packType === PACK_TYPE.HYBRID_PACK
          ? product?.multiPackVariationsAvailable?.reduce(
              (acc, variation) => acc + variation.amountVariationsToChoose,
              0,
            )
          : product?.amountVariationsPerAdvancedPack,
    };
  }, [product, journeySlug]);

  const renderRegularItem = () => (
    <div className="shop-overall-carousel">
      <a
        className="product-slug"
        href={`/shop/product/${product.slug}${
          journeySlug ? `?journey=${journeySlug}` : ''
        }`}
      >
        <div className="shop-overall-carousel-shop-item text-left">
          <div className="img-new-products">
            <ShopProductImage product={product} />
            {renderDiscount()}
          </div>
          <div className="product-text">
            <div className="shop-product-wrapper">
              <span className="shop-product-name">{product.name}</span>
            </div>
            <div className={`${onBuyNow ? 'onBuyNowWrapper' : ''}`}>
              <div className="shop-product-money">
                {productVariations?.length === 1 &&
                (journeySlug
                  ? !isHybridPack
                  : product?.pack?.packType !== PACK_TYPE.HYBRID_PACK) ? (
                  <div className="shop-price-list">
                    <PriceDisplay
                      t={t}
                      userPrice={productVariations[0]?.userPrice}
                      userPromotionPrice={
                        productVariations[0]?.userPromotionPrice
                      }
                    />
                  </div>
                ) : (
                  <div className="shop-price-list">
                    <div className="main-price">
                      {t(
                        'price.starting.from',
                        returnPricesRange(productVariations).mainPriceRange,
                      )}
                    </div>
                    <div className="minor-price">
                      {t('price.starting.from', minorPrice)}
                    </div>
                  </div>
                )}
              </div>
              {onBuyNow && !isAdvancedPack && !isHybridPack ? (
                <button
                  type="button"
                  onClick={evt => {
                    evt.preventDefault();
                    evt.stopPropagation();
                    onBuyNow();
                  }}
                  className="btn m-btn m-btn--pill btn-dynamic onBuyNowBtn"
                >
                  {t('shop.buyNow')}
                </button>
              ) : null}
              {!isLandingPagePromotion && (isAdvancedPack || isHybridPack) && (
                <div className="journey-advanced-pack">
                  <AddEditSection
                    t={t}
                    isEditable={isEditable}
                    openModal={() => onOpenAdvancedPackModal(product)}
                    pack={modPack}
                    product={product}
                    isJourney={!!journeySlug}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </a>
    </div>
  );

  return (
    <>{isLandingPagePromotion ? renderPromotionItem() : renderRegularItem()}</>
  );
}

ShopProductItem.propTypes = {
  t: PropTypes.func.isRequired,
  product: PropTypes.shape(ProductProp.propType).isRequired,
  isLandingPagePromotion: PropTypes.bool,
  onBuyNow: PropTypes.func,
  onOpenAdvancedPackModal: PropTypes.func,
  loggedUser: PropTypes.shape(UserProp.propType),
  journeySlug: PropTypes.string,
};

ShopProductItem.defaultProps = {
  isLandingPagePromotion: false,
  onBuyNow: null,
  onOpenAdvancedPackModal: null,
  loggedUser: null,
  journeySlug: null,
};

export default translate(ShopProductItem);
